import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JalurJenis, Spk, Unitkerja } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import { getListJalurJenis, getListUnitkerja } from "@/services/getData"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import Combobox from "@/components/Combobox"

const postData = async (newData: Spk) => {
  const response = await api.post("/master/spk/store", newData)
  return response.data
}

const patchData = async (newData: Spk) => {
  const response = await api.patch("/master/spk/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_spk: string }) => {
  const response = await api({
    url: "/master/spk/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: Spk | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<Spk>({
    id_ref_spk: selectedRow?.id_ref_spk || "",
    id_ref_jalur_jenis: selectedRow?.id_ref_jalur_jenis || "",
    nominal_spk: selectedRow?.nominal_spk || "",
    tahun: selectedRow?.tahun || "",
    tipe_spk: selectedRow?.tipe_spk || "",
    uk_id: selectedRow?.uk_id || "",
  })
  const [openListProdi, setOpenListProdi] = useState(false)

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const { data: dataJalurJenis } = useQuery({
    queryKey: ["listJalurJenis"],
    queryFn: getListJalurJenis,
  })

  const { data: dataProdi } = useQuery({
    queryKey: ["listUnitkerja"],
    queryFn: getListUnitkerja,
    select: (data) =>
      data?.map((unitkerja: Unitkerja) => ({
        label: unitkerja.nama,
        value: `${unitkerja.uk_id}`,
        unitkerjaGroupKey: unitkerja.unitkerja_group_key,
        isActive: unitkerja.is_active,
      })),
  })

  const filteredDataProdi = dataProdi?.filter(
    (option: {
      label: string
      value: string
      unitkerjaGroupKey: string
      isActive: boolean
    }) => {
      return option?.unitkerjaGroupKey == "5" && option.isActive
    }
  )

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listSpk"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Spk"
          description={`Apakah anda yakin akan menghapus data spk "${selectedRow?.nominal_spk}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data SPK" : "Update Data SPK"}
      />
      <div>
        <Label>Jalur Jenis</Label>
        <Select
          value={form.id_ref_jalur_jenis}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              id_ref_jalur_jenis: value,
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Jalur Jenis">
              {form.id_ref_jalur_jenis &&
                dataJalurJenis?.find(
                  (jalurJenis: JalurJenis) =>
                    jalurJenis.id_ref_jalur_jenis === form.id_ref_jalur_jenis
                )?.nama_jalur_jenis}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {dataJalurJenis?.map((jalurJenis: JalurJenis, index: number) => (
              <SelectItem
                key={index}
                value={`${jalurJenis.id_ref_jalur_jenis}`}
              >
                {jalurJenis?.nama_jalur_jenis}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Prodi</Label>
        <Combobox
          open={openListProdi}
          setOpen={setOpenListProdi}
          value={form.uk_id || ""}
          setValue={(value) => {
            setForm((prev) => ({ ...prev, uk_id: value }))
          }}
          options={filteredDataProdi}
          placeholder="Pilih prodi"
        />
      </div>

      <div>
        <Label>Tahun</Label>
        <Input
          required
          minLength={4}
          maxLength={4}
          placeholder="Ketik tahun..."
          value={form.tahun}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, tahun: event.target.value }))
          }
        />
      </div>

      <div>
        <Label>Tipe SPK</Label>
        <Select
          value={form.tipe_spk}
          onValueChange={(value) =>
            setForm((prev) => ({
              ...prev,
              tipe_spk: value as "spk1" | "spk2" | "spk3" | "spk4" | "spk5",
            }))
          }
        >
          <SelectTrigger>
            <SelectValue placeholder="Pilih Tipe SPK" />
          </SelectTrigger>
          <SelectContent>
            {["spk1", "spk2", "spk3", "spk4", "spk5"]?.map(
              (value: string, index: number) => (
                <SelectItem key={index} value={`${value}`}>
                  {value}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>Nominal SPK</Label>
        <Input
          required
          placeholder="Ketik nominal spk..."
          value={form.nominal_spk}
          onChange={(event) =>
            setForm((prev) => ({ ...prev, nominal_spk: event.target.value }))
          }
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
